// Shared first-touch UTM attribution helper.
//
// Problem this solves: several internal links/CTAs across the site link to
// the demo form with a hardcoded utm_source/medium/campaign (e.g. "website /
// organic"). Previously every form trusted the URL query string over
// localStorage, so a visitor who arrived from a real ad, then clicked one of
// those internal links before filling the form, had their real ad
// attribution silently overwritten.
//
// Fix: utm_source / utm_medium / utm_campaign are captured once per
// attribution window (first touch wins) and never overwritten by a later
// internal link. utm_content is intentionally exempt from the lock because
// it's reused across the site to tag *which* button/section opened the form
// (e.g. "navbar_form", "cloud_hero_section"), which is expected to update on
// every click.

const STORAGE_KEYS = {
  source: "utm_source",
  medium: "utm_medium",
  campaign: "utm_campaign",
  content: "utm_content",
};

const FIRST_TOUCH_AT_KEY = "utm_first_touch_at";
const FIRST_TOUCH_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const safeGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // localStorage unavailable (private mode, etc.) - fail silently.
  }
};

const isFirstTouchExpired = () => {
  const capturedAt = safeGet(FIRST_TOUCH_AT_KEY);
  if (!capturedAt) return true;
  return Date.now() - Number(capturedAt) > FIRST_TOUCH_TTL_MS;
};

/**
 * Reads UTM params from a query string and persists them to localStorage.
 * Call this as early as possible on every navigation (route change or full
 * page load) so attribution is captured before any form mounts.
 */
export const captureUtmParams = (search = window.location.search) => {
  const params = new URLSearchParams(search);
  const urlSource = params.get("utm_source");
  const urlContent = params.get("utm_content");

  const alreadyLocked = !!safeGet(STORAGE_KEYS.source) && !isFirstTouchExpired();

  if (urlSource && !alreadyLocked) {
    safeSet(STORAGE_KEYS.source, urlSource);
    safeSet(STORAGE_KEYS.medium, params.get("utm_medium") || "");
    safeSet(STORAGE_KEYS.campaign, params.get("utm_campaign") || "");
    safeSet(FIRST_TOUCH_AT_KEY, String(Date.now()));
  }

  // utm_content is allowed to keep updating so per-CTA tracking still works.
  if (urlContent) {
    safeSet(STORAGE_KEYS.content, urlContent);
  }
};

/** Returns the currently persisted UTM values for use in form state/submission. */
export const getUtmValues = () => ({
  utm_source: safeGet(STORAGE_KEYS.source) || "",
  utm_medium: safeGet(STORAGE_KEYS.medium) || "",
  utm_campaign: safeGet(STORAGE_KEYS.campaign) || "",
  utm_content: safeGet(STORAGE_KEYS.content) || "",
});

/**
 * Opens a Tally popup (https://developers.tally.so/widgets/popups) with the
 * visitor's persisted UTM values passed as hidden fields.
 *
 * Tally forms are hosted externally and only receive attribution data that's
 * explicitly passed in - the static `#tally-open=ID` anchor links used across
 * the site never carried any UTM data, so every Tally submission lost
 * attribution. The corresponding Tally forms must have hidden fields named
 * utm_source / utm_medium / utm_campaign / utm_content configured in the
 * Tally form builder for these values to actually show up in submissions.
 */
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content"];

// Tally hash options we can faithfully map to Tally.openPopup(). If a link
// carries any other tally-* option we leave it to Tally's own handler.
const HASH_OPTIONS_HANDLED = ["tally-open", "tally-emoji-text", "tally-emoji-animation"];

const hasAnyUtm = () => Object.values(getUtmValues()).some(Boolean);

const handleTallyClick = (event) => {
  try {
    const anchor = event.target && event.target.closest && event.target.closest("a[href]");
    if (!anchor) return;
    const href = anchor.getAttribute("href") || "";
    if (!hasAnyUtm()) return;

    // 1) Popup links: <a href="#tally-open=ID&tally-emoji-text=...">
    const hashIndex = href.indexOf("#tally-open=");
    if (hashIndex !== -1 && window.Tally) {
      const hashParams = new URLSearchParams(href.slice(hashIndex + 1));
      const onlyHandled = Array.from(hashParams.keys()).every((k) => HASH_OPTIONS_HANDLED.includes(k));
      const formId = hashParams.get("tally-open");
      if (formId && onlyHandled) {
        const options = {};
        const emojiText = hashParams.get("tally-emoji-text");
        if (emojiText) {
          options.emoji = { text: emojiText, animation: hashParams.get("tally-emoji-animation") || "wave" };
        }
        openTallyPopup(formId, options);
        // Popup opened with UTMs: stop Tally's default handler (no double popup).
        event.preventDefault();
        event.stopImmediatePropagation();
      }
      return;
    }

    // 2) Direct form links: https://tally.so/r/ID(?...)
    if (/^https?:\/\/(www\.)?tally\.so\/r\//i.test(href)) {
      const url = new URL(href);
      const stored = getUtmValues();
      UTM_KEYS.forEach((key) => {
        // Visitor's real attribution wins over a hardcoded value on the link.
        if (stored[key]) url.searchParams.set(key, stored[key]);
      });
      anchor.setAttribute("href", url.toString());
    }
  } catch {
    // Never interfere with the click if anything goes wrong.
  }
};

const patchTallyIframes = () => {
  try {
    if (!hasAnyUtm()) return;
    const stored = getUtmValues();
    document.querySelectorAll('iframe[src*="tally.so/"]').forEach((frame) => {
      const url = new URL(frame.src);
      const missing = UTM_KEYS.some((key) => stored[key] && url.searchParams.get(key) !== stored[key]);
      if (!missing) return;
      UTM_KEYS.forEach((key) => {
        if (stored[key]) url.searchParams.set(key, stored[key]);
      });
      frame.src = url.toString();
    });
  } catch {
    // ignore
  }
};

/**
 * Installs site-wide Tally attribution so every Tally popup link, direct
 * form link and embedded form receives the visitor's saved UTMs, without
 * editing each link. Returns a cleanup function.
 */
export const installTallyUtmHandlers = () => {
  window.addEventListener("click", handleTallyClick, true);
  return () => window.removeEventListener("click", handleTallyClick, true);
};

/** Call after route changes so Tally iframes on the new page get the UTMs. */
export const applyUtmToTallyIframes = () => {
  patchTallyIframes();
  setTimeout(patchTallyIframes, 1000);
};

export const openTallyPopup = (formId, options = {}) => {
  if (typeof window === "undefined" || !window.Tally) return;

  const { hiddenFields, ...rest } = options;
  window.Tally.openPopup(formId, {
    ...rest,
    hiddenFields: {
      ...getUtmValues(),
      ...hiddenFields,
    },
  });
};
